import React, { useState, useRef, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Download,
  ArrowRight,
  Star,
  Check,
  Bell,
  MapPin,
  Heart,
  ChevronRight,
  Smartphone,
  Shield,
  Clock,
  Home,
  Calendar,
  CreditCard,
  Sparkles,
  Crown,
  Leaf,
  Trees,
  Mountain,
  Sun,
  Bike,
  Dumbbell,
  Dog,
  Sprout,
} from "lucide-react";

const features = [
  {
    icon: <Leaf className="w-5 h-5" />,
    title: "LEED-Certified Community",
    description:
      "The Valley's first LEED-certified neighborhood setting the standard for eco-conscious living",
    image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2RbWzCDGVIgpftxZT8uVfdc2z00gtxmDmVA&s",
    color: "from-gold-200 to-gold-100",
    gradient: "from-gold-500 to-gold-400",
  },
  {
    icon: <Trees className="w-5 h-5" />,
    title: "Nature-Inspired Living",
    description:
      "gold forests, flower farms and orchard groves at your doorstep",
    image: "https://media.cnn.com/api/v1/images/stellar/prod/190522111542-cityland-mall-europe-theme.jpg?q=w_4000,h_2158,x_0,y_0,c_fill/h_447",
    color: "from-green-200 to-green-100",
    gradient: "from-green-500 to-green-400",
  },
  {
    icon: <Bike className="w-5 h-5" />,
    title: "Active Lifestyle",
    description:
      "Jogging routes, fitness areas and sports facilities for an energetic life",
    image: "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg",
    color: "from-blue-200 to-blue-100",
    gradient: "from-blue-500 to-blue-400",
  },
  {
    icon: <Dog className="w-5 h-5" />,
    title: "Pet-Friendly Community",
    description:
      "Dedicated dog parks and pet-friendly spaces throughout Rivera",
    image: "https://images.pexels.com/photos/1571458/pexels-photo-1571458.jpeg",
    color: "from-amber-200 to-amber-100",
    gradient: "from-amber-500 to-amber-400",
  },
  {
    icon: <Sprout className="w-5 h-5" />,
    title: "Sustainable Amenities",
    description: "Flower farms, meandering river and native planting trails",
    image: "https://images.pexels.com/photos/1571463/pexels-photo-1571463.jpeg",
    color: "from-teal-200 to-teal-100",
    gradient: "from-teal-500 to-teal-400",
  },
  {
    icon: <Dumbbell className="w-5 h-5" />,
    title: "Sports Facilities",
    description:
      "Tennis, padel, basketball courts and soccer pitch for active residents",
    image: "https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg",
    color: "from-red-200 to-red-100",
    gradient: "from-red-500 to-red-400",
  },
];

const phoneScreens = [
  {
    image: "/gallery/bg-1.jpg",
    title: "Community Map",
    color: "bg-gradient-to-br from-gold-500 to-gold-400",
    icon: <MapPin className="w-6 h-6" />,
  },
  {
    image: "/gallery/bg-2.jpg",
    title: "Amenities Booking",
    color: "bg-gradient-to-br from-blue-500 to-blue-400",
    icon: <Calendar className="w-6 h-6" />,
  },
  {
    image: "/gallery/bg-3.jpg",
    title: "Nature Trails",
    color: "bg-gradient-to-br from-green-500 to-green-400",
    icon: <Trees className="w-6 h-6" />,
  },
  {
    image: "/gallery/bg-4.jpg",
    title: "Sports Facilities",
    color: "bg-gradient-to-br from-amber-500 to-amber-400",
    icon: <Dumbbell className="w-6 h-6" />,
  },
  {
    image: "/gallery/bg-5.jpg",
    title: "Sustainability",
    color: "bg-gradient-to-br from-teal-500 to-teal-400",
    icon: <Leaf className="w-6 h-6" />,
  },
];

const DigitalExperience = () => {
  const [activeScreen, setActiveScreen] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.2 });

  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setActiveScreen((prev) => (prev + 1) % phoneScreens.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isHovered]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen py-20 overflow-hidden bg-gradient-to-b from-gray-50 to-gray-100"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)`,
          }}
        />

        {/* Floating particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gold-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gold-100 mb-6"
          >
            <Sparkles className="w-5 h-5 text-gold-500 mr-2" />
            <span className="text-gold-600 font-medium">Rivera Living</span>
          </motion.div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gold-500 to-gold-600 bg-clip-text text-transparent">
              Green Living
            </span>
            <br />
            <span className="text-gray-900">Redefined</span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Experience sustainable community living through our digital platform
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-1 gap-16 items-center">
          {/* Phone Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto"
            style={{ perspective: "1000px" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <motion.div
              className="relative  mr-[100px] w-[250px] h-[500px] sm:w-[280px] sm:h-[580px] md:w-[900px] md:h-[650px]"
              animate={{
                rotateY: isHovered ? 0 : [0, -5, 5, 0],
                transition: {
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            >
              {/* Phone Frame */}
              <motion.div
                className={`absolute inset-0 ${phoneScreens[activeScreen].color} rounded-[40px] p-2`}
                animate={{
                  boxShadow: [
                    "0 20px 40px rgba(0,0,0,0.1)",
                    "0 30px 60px rgba(0,0,0,0.15)",
                    "0 20px 40px rgba(0,0,0,0.1)",
                  ],
                }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="w-full h-full rounded-[35px] overflow-hidden border-4 border-white/20 relative bg-black">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-7 bg-black rounded-b-xl z-20" />

                  {/* Screen Content */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeScreen}
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0"
                    >
                      {/* Fixed image container */}
                      <div className="absolute inset-0 w-full h-full">
                        <img
                          src={phoneScreens[activeScreen].image}
                          alt={phoneScreens[activeScreen].title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Screen Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent">
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-center space-x-3 mb-2">
                            <div
                              className={`p-2 rounded-lg ${phoneScreens[activeScreen].color}`}
                            >
                              {phoneScreens[activeScreen].icon}
                            </div>
                            <h3 className="text-xl font-semibold text-white">
                              {phoneScreens[activeScreen].title}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Screen Indicators */}
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                    {phoneScreens.map((_, index) => (
                      <motion.button
                        key={index}
                        onClick={() => setActiveScreen(index)}
                        className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                          activeScreen === index ? "bg-white" : "bg-white/30"
                        }`}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ delay: index * 0.1 }}
                className={`group relative overflow-hidden rounded-2xl p-6 bg-white hover:bg-gradient-to-br ${feature.gradient} transition-all duration-500`}
              >
                <div className="relative z-10 cursor-pointer">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} group-hover:bg-white/10 flex items-center justify-center mb-4 transition-colors duration-500`}
                  >
                    <div className="text-white">{feature.icon}</div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors duration-500">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/80 transition-colors duration-500">
                    {feature.description}
                  </p>
                </div>

                <motion.div
                  className="absolute inset-0 -z-10 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
                  style={{ filter: "blur(2px)" }}
                >
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        
      </div>
    </section>
  );
};

export default DigitalExperience;